import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './InProductionList.css';

function InProductionList() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [groupBy, setGroupBy] = useState('sku'); // 'sku' or 'workorder'
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchInProduction();
    const interval = setInterval(fetchInProduction, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const fetchInProduction = async () => {
    try {
      const response = await fetch('/api/in-production');
      const data = await response.json();
      setItems(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching in-production:', err);
      setLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'carting': 'Carting',
      'wicking': 'Wicking',
      'pouring': 'Pouring',
      'packaging': 'Packaging'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      'carting': '#3182ce',
      'wicking': '#805ad5',
      'pouring': '#dd6b20',
      'packaging': '#38a169'
    };
    return colors[status] || '#718096';
  };

  // Filter items
  const filteredItems = filterStatus === 'all'
    ? items
    : items.filter(item => item.status === filterStatus);

  // Group items
  const groupedItems = {};
  filteredItems.forEach(item => {
    const key = groupBy === 'sku' ? item.item_sku : item.work_order_number;
    if (!groupedItems[key]) {
      groupedItems[key] = {
        key,
        item_sku: item.item_sku,
        item_description: item.item_description,
        work_order_number: item.work_order_number,
        carts: [],
        totalUnits: 0,
        statuses: new Set()
      };
    }
    groupedItems[key].carts.push({
      barcode: item.cart_barcode,
      status: item.status,
      units: item.units
    });
    groupedItems[key].totalUnits += item.units;
    groupedItems[key].statuses.add(item.status);
  });

  const groupedArray = Object.values(groupedItems).sort((a, b) => {
    // Sort by status priority then by SKU
    const statusOrder = { 'packaging': 0, 'pouring': 1, 'wicking': 2, 'carting': 3 };
    const aStatus = [...a.statuses][0];
    const bStatus = [...b.statuses][0];
    const statusDiff = (statusOrder[aStatus] || 99) - (statusOrder[bStatus] || 99);
    if (statusDiff !== 0) return statusDiff;
    return a.item_sku.localeCompare(b.item_sku);
  });

  // Get unique statuses for filter
  const availableStatuses = [...new Set(items.map(i => i.status))];

  if (loading) {
    return (
      <div className="in-production-list loading">
        <div className="spinner"></div>
        <p>Loading production queue...</p>
      </div>
    );
  }

  return (
    <div className="in-production-list">
      <div className="list-header">
        <h1>In Production</h1>
        <div className="header-controls">
          <div className="filter-group">
            <label>Status:</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">All Stages</option>
              {availableStatuses.map(s => (
                <option key={s} value={s}>{getStatusLabel(s)}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Group by:</label>
            <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
              <option value="sku">SKU</option>
              <option value="workorder">Work Order</option>
            </select>
          </div>
        </div>
      </div>

      {/* Summary bar */}
      <div className="summary-bar">
        <div className="summary-stat">
          <span className="stat-value">{groupedArray.length}</span>
          <span className="stat-label">{groupBy === 'sku' ? 'Items' : 'Work Orders'}</span>
        </div>
        <div className="summary-stat">
          <span className="stat-value">{filteredItems.reduce((sum, i) => sum + i.units, 0).toLocaleString()}</span>
          <span className="stat-label">Total Units</span>
        </div>
        <div className="summary-stat">
          <span className="stat-value">{new Set(filteredItems.map(i => i.cart_barcode)).size}</span>
          <span className="stat-label">Carts</span>
        </div>
      </div>

      {/* Items list */}
      <div className="production-items">
        {groupedArray.length === 0 ? (
          <div className="empty-state">
            <p>No items currently in production</p>
          </div>
        ) : (
          groupedArray.map(group => (
            <div key={group.key} className="production-item">
              <div className="item-main">
                <div className="item-info">
                  <div className="item-sku">{group.item_sku}</div>
                  <div className="item-desc">{group.item_description}</div>
                  {groupBy === 'sku' && group.work_order_number && (
                    <div className="item-wo">
                      WO: <Link to={`/workorders/${group.work_order_number}`}>{group.work_order_number}</Link>
                    </div>
                  )}
                  {groupBy === 'workorder' && (
                    <div className="item-wo">
                      Work Order: <Link to={`/workorders/${group.work_order_number}`}>{group.work_order_number}</Link>
                    </div>
                  )}
                </div>
                <div className="item-units">
                  <span className="units-value">{group.totalUnits.toLocaleString()}</span>
                  <span className="units-label">units</span>
                </div>
              </div>

              <div className="item-carts">
                {group.carts.map((cart, idx) => (
                  <div key={idx} className="cart-chip" style={{ borderColor: getStatusColor(cart.status) }}>
                    <span className="cart-status" style={{ background: getStatusColor(cart.status) }}>
                      {getStatusLabel(cart.status)}
                    </span>
                    <span className="cart-barcode">{cart.barcode}</span>
                    <span className="cart-units">{cart.units}</span>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default InProductionList;
